import React from "react";
import { DialogProps, ChannelInfo } from "../../types/dialog-types";
import { tagSelectStyles as s } from "./tagSelectDialogStyles";
import { getTigerResource } from "../../utils/tigerResource";
import LangDateInput from "../LangDateInput";
import { tigerSecondsToDate, validateEndAfterStart, validateMaxDuration } from "../../utils/tigerTime";
import { useTigerMaxReplay } from "../../utils/tigerMaxReplay";

interface HistDiagSelectTagDialogProps extends DialogProps {
  tags?: ChannelInfo[];
  /** Diagnostic message time (Tiger seconds); pre-fills the range to ±5 min. */
  msgTime?: number;
}

// Tiger seconds -> "YYYY-MM-DD" / "HH:MM:SS" (UTC, matching the graph windows).
function toInputFields(tigerSecs: number): { date: string; time: string } {
  const d = tigerSecondsToDate(tigerSecs);
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    date: `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`,
    time: `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`,
  };
}

/** Tag-selection dialog for the diagnostics "Tools → Historical Graph" action:
 *  a start/end date+time range plus the message's tags. Themed to match the
 *  Graph Channel Selection dialog; labels via getTigerResource. */
export function HistDiagSelectTagDialog({
  open,
  onClose,
  onApply,
  tags = [],
  msgTime,
}: HistDiagSelectTagDialogProps) {
  const [selected, setSelected] = React.useState<Set<number>>(new Set());
  const [startDate, setStartDate] = React.useState<string>("");
  const [startTime, setStartTime] = React.useState<string>("00:00:00");
  const [endDate, setEndDate] = React.useState<string>("");
  const [endTime, setEndTime] = React.useState<string>("23:59:59");
  const maxReplay = useTigerMaxReplay();

  React.useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    if (msgTime == null) return;
    const start = toInputFields(msgTime - 300);
    const end = toInputFields(msgTime + 300);
    setStartDate(start.date);
    setStartTime(start.time);
    setEndDate(end.date);
    setEndTime(end.time);
  }, [open, msgTime]);

  const toggleRow = (idx: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const handleOk = () => {
    const chosen = tags.filter((_, idx) => selected.has(idx));
    if (chosen.length > 0) {
      const rangeErr =
        startDate && endDate
          ? validateEndAfterStart(
              `${startDate}T${startTime || "00:00:00"}`,
              `${endDate}T${endTime || "00:00:00"}`,
            )
          : null;
      if (rangeErr) {
        alert(rangeErr);
        return;
      }
      if (startDate && endDate) {
        const startSecs = Date.parse(`${startDate}T${startTime || "00:00:00"}Z`) / 1000;
        const endSecs = Date.parse(`${endDate}T${endTime || "00:00:00"}Z`) / 1000;
        const durErr = validateMaxDuration(endSecs - startSecs, maxReplay);
        if (durErr) {
          alert(durErr);
          return;
        }
      }
      onApply?.({ tags: chosen, startDate, startTime, endDate, endTime });
    }
    onClose();
  };

  if (!open) return null;
  const title = getTigerResource("TIGER_GRAPH_CHANNEL_HIST_GRAPH_TITLE");

  return (
    <div style={s.overlay}>
      <div style={s.dialog} role="dialog" aria-modal="true" aria-label={title}>
        <div style={s.header}>
          <span style={s.title}>{title}</span>
          <button style={s.closeBtn} onClick={onClose}>
            &#x2715;
          </button>
        </div>

        <div style={s.body}>
          <div style={s.card}>
            <div style={s.fieldGroup}>
              <div style={s.field}>
                <label style={s.fieldLabel}>{getTigerResource("TIGER_RES_START_TIME")}</label>
                <input style={s.input} type="time" step="1" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
              </div>
              <div style={s.field}>
                <label style={s.fieldLabel}>{getTigerResource("TIGER_RES_START_DATE")}</label>
                <LangDateInput style={s.input} value={startDate} onChange={setStartDate} />
              </div>
            </div>
            <div style={s.fieldGroup}>
              <div style={s.field}>
                <label style={s.fieldLabel}>{getTigerResource("TIGER_RES_END_TIME")}</label>
                <input style={s.input} type="time" step="1" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
              </div>
              <div style={s.field}>
                <label style={s.fieldLabel}>{getTigerResource("TIGER_RES_END_DATE")}</label>
                <LangDateInput style={s.input} value={endDate} onChange={setEndDate} />
              </div>
            </div>
          </div>

          <div style={s.section}>
            <div style={s.tableWrap}>
              <div className="list-control" style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 180 }}>{getTigerResource("TIGER_EXPORT_CHANNEL_NAME")}</th>
                      <th style={{ width: 300 }}>{getTigerResource("TIGER_EXPORT_CHANNEL_DESCR")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tags.map((item, idx) => (
                      <tr
                        key={idx}
                        className={selected.has(idx) ? "selected" : ""}
                        onClick={() => toggleRow(idx)}
                      >
                        <td>{item.tag}</td>
                        <td>{item.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div style={s.footer}>
          <button
            style={{ ...s.btnPrimary, opacity: selected.size === 0 ? 0.4 : 1 }}
            onClick={handleOk}
            disabled={selected.size === 0}
          >
            {getTigerResource("TIGER_RES_OK")}
          </button>
          <button style={s.btnSecondary} onClick={onClose}>
            {getTigerResource("TIGER_RES_CANCEL")}
          </button>
        </div>
      </div>
    </div>
  );
}
